// ── OpportunityToTermSheetPage — funnel step: Opportunities → Term Sheets Issued ──
import { useState, useEffect, useMemo } from "react";
import { B } from "../../../shared/theme";
import { fmt, exportCSV } from "../../../shared/format";
import { Btn, Card, ErrorMsg, ExportBtn, STh, SectionHeader, Spinner, Td, TdNum, Th } from "../../../shared/ui";
import { useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";
import { PivotMatrix, KpiCard, pctLabel, parseDate, isExcludedOwner, OwnerMultiSelect } from "./analyticsShared";

const initf = (x) => parseFloat(x.initial_funding_amount || 0);

export const OpportunityToTermSheetPage = () => {
  const { api } = useApp();
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [created, setCreated] = useState({ from: "", to: "" });
  const [company, setCompany] = useState("");
  const [sourceSel, setSourceSel] = useState("");
  const [ownerSel, setOwnerSel] = useState(null);   // Set multi-select; null = default (all except excluded four)
  const [stageSel, setStageSel] = useState("");
  const [empSel, setEmpSel] = useState("");

  const load = () => {
    setLoading(true);
    api.get("getOpportunities").then(o => { setOpps(Array.isArray(o) ? o : []); setLoading(false); }).catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const owners = useMemo(() => [...new Set(opps.map(o => o.ownerName).filter(Boolean))].sort(), [opps]);
  const sources = useMemo(() => [...new Set(opps.map(o => o.lead_source).filter(Boolean))].sort(), [opps]);
  const stages = useMemo(() => [...new Set(opps.map(o => o.stage).filter(Boolean))].sort(), [opps]);

  const fromD = created.from ? new Date(created.from) : null;
  const toD = created.to ? new Date(created.to) : null;
  const inCreated = (v) => { if (!fromD && !toD) return true; const d = parseDate(v); if (!d) return false; if (fromD && d < fromD) return false; if (toD && d > toD) return false; return true; };

  // default Sourced By to every owner except the excluded four
  useEffect(() => { if (ownerSel === null && owners.length) setOwnerSel(new Set(owners.filter(o => !isExcludedOwner(o)))); }, [owners, ownerSel]);
  const ownerOk = (name) => !name ? true : (ownerSel ? ownerSel.has(name) : !isExcludedOwner(name));

  const oppsF = useMemo(() => {
    const c = company.trim().toLowerCase();
    return opps.filter(o => {
      if (!ownerOk(o.ownerName)) return false;
      if (c && !((o.companyname || o.name || "").toLowerCase().includes(c))) return false;
      if (sourceSel && o.lead_source !== sourceSel) return false;
      if (stageSel && o.stage !== stageSel) return false;
      if (!inCreated(o.createdat)) return false;
      return true;
    });
  }, [opps, company, sourceSel, ownerSel, stageSel, created]);

  // empSel highlights the matrices (which keep every employee) but filters the KPI + detail
  const oppsSel = useMemo(() => empSel ? oppsF.filter(o => (o.ownerName || "—") === empSel) : oppsF, [oppsF, empSel]);

  const { sorted: detailSorted, sortKey, sortDir, toggle } = useSortable(oppsSel, 'companyname', 'asc');

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  const issuedAll = oppsF.filter(o => o.term_sheet_issued_date);   // matrix (all employees)
  const issued = oppsSel.filter(o => o.term_sheet_issued_date);    // KPI + detail (selected)
  const selStyle = { padding: "6px 10px", fontSize: 13, border: "1px solid var(--color-border-secondary)", borderRadius: 6, background: "var(--color-background-primary)", color: "var(--color-text-primary)" };
  const cardTitle = { fontSize: 11, fontWeight: 700, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.06em", padding: "12px 14px 8px" };
  const pick = (k) => setEmpSel(prev => prev === k ? "" : k);

  return <>
    <SectionHeader title="Opportunity to Term Sheet" />

    <div style={{ display: "flex", gap: 16, marginBottom: 16, alignItems: "stretch" }}>
      {/* filters — two stacked cards, left */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 12 }}>
        <Card style={{ padding: 10, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <input value={company} onChange={e => setCompany(e.target.value)} placeholder="Company…" style={{ ...selStyle, minWidth: 150 }} />
          <select value={sourceSel} onChange={e => setSourceSel(e.target.value)} style={selStyle}><option value="">All Lead Sources</option>{sources.map(s => <option key={s} value={s}>{s}</option>)}</select>
          <OwnerMultiSelect owners={owners} value={ownerSel} onChange={setOwnerSel} />
          <select value={stageSel} onChange={e => setStageSel(e.target.value)} style={selStyle}><option value="">All Stages</option>{stages.map(s => <option key={s} value={s}>{s}</option>)}</select>
          {empSel && <button onClick={() => setEmpSel("")} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", fontSize: 12, fontWeight: 600, border: "1px solid " + B.cyan, borderRadius: 999, background: B.cyanLight, color: B.cyanDark, cursor: "pointer" }}>{empSel} <span style={{ fontWeight: 700 }}>✕</span></button>}
        </Card>
        <Card style={{ padding: 10, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--color-text-secondary)" }}>
            Opp Created
            <input type="date" value={created.from} onChange={e => setCreated({ ...created, from: e.target.value })} style={selStyle} />
            <span>–</span>
            <input type="date" value={created.to} onChange={e => setCreated({ ...created, to: e.target.value })} style={selStyle} />
          </label>
        </Card>
      </div>
      {/* KPI — standalone card, right */}
      <div style={{ flex: "0 0 240px" }}>
        <KpiCard label="Opp → Term Sheet Conversion" value={pctLabel(issued.length, oppsSel.length)} sub={issued.length + " of " + oppsSel.length + " opps"} />
      </div>
    </div>

    <PivotMatrix title="Opportunities (by created month)" items={oppsF} rowKeyOf={o => o.ownerName} dateOf={o => o.createdat} onRowClick={pick} activeRow={empSel} />
    <PivotMatrix title="Term Sheets Issued (by issued month)" items={issuedAll} rowKeyOf={o => o.ownerName} dateOf={o => o.term_sheet_issued_date} onRowClick={pick} activeRow={empSel} />

    <Card style={{ overflowX: "auto" }}>
        <div style={{ ...cardTitle, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <span>Opportunity Detail</span>
          <ExportBtn onClick={() => exportCSV(detailSorted, ["ownerName", "companyname", "term_sheet_issued_date", "stage", "termination_reason", "initial_funding_amount"], "opp_to_term_sheet.csv")}>Export CSV</ExportBtn>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap", fontSize: 12 }}>
          <thead><tr>
            <STh label="Sourced By"      sortKey="ownerName"              currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Opportunity Name" sortKey="companyname"           currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <Th style={{ textAlign: "right" }}>Opp Cnt</Th>
            <STh label="TS Cnt"          sortKey="term_sheet_issued_date" currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
            <STh label="Stage"           sortKey="stage"                  currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Termination Reason" sortKey="termination_reason"  currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Initial Amount"  sortKey="initial_funding_amount" currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
          </tr></thead>
          <tbody>{detailSorted.length === 0 ? <tr><Td colSpan={7} style={{ color: "var(--color-text-secondary)" }}>No opportunities.</Td></tr> : detailSorted.map(o => (
            <tr key={o.id} onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"} onMouseLeave={e => e.currentTarget.style.background = ""}>
              <Td>{o.ownerName || "—"}</Td>
              <Td style={{ fontWeight: 500, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={o.companyname || o.name || ""}>{o.companyname || o.name || "—"}</Td>
              <TdNum>1</TdNum>
              <TdNum>{o.term_sheet_issued_date ? 1 : ""}</TdNum>
              <Td>{o.stage || "—"}</Td>
              <Td>{o.termination_reason || "—"}</Td>
              <TdNum>{fmt(initf(o))}</TdNum>
            </tr>
          ))}</tbody>
          <tfoot><tr style={{ background: "var(--color-background-secondary)" }}>
            <td colSpan={2} style={{ padding: "10px 12px", fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)" }}>Total ({detailSorted.length})</td>
            <td style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{detailSorted.length}</td>
            <td style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{issued.length}</td>
            <td colSpan={2} style={{ borderTop: "1.5px solid var(--color-border-secondary)" }} />
            <td style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{fmt(detailSorted.reduce((s, o) => s + initf(o), 0))}</td>
          </tr></tfoot>
        </table>
      </Card>
  </>;
};
